import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  Chip,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material'
import SaveIcon from '@mui/icons-material/Save'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import DeleteIcon from '@mui/icons-material/Delete'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { supabase } from '../../config/supabase'
import { useAuth } from '../../hooks/useAuth'
import RichTextEditor from '../../components/RichTextEditor/RichTextEditor'
import { Article } from '../../types'
import { articleUrl, slugify, SLUG_PATTERN, useArticleCategories } from './articleData'

const DEFAULT_AUTHOR = 'Tobias Dahl'

interface ArticleFormData {
  title: string
  slug: string
  category: string // '' = ingen kategori
  extra_categories: string[]
  excerpt: string
  seo_title: string
  seo_metadesc: string
  author: string
  published: boolean
  published_at: string | null // ISO timestamp
  content: string
}

const EMPTY_FORM: ArticleFormData = {
  title: '',
  slug: '',
  category: '',
  extra_categories: [],
  excerpt: '',
  seo_title: '',
  seo_metadesc: '',
  author: DEFAULT_AUTHOR,
  published: false,
  published_at: null,
  content: '',
}

const toFormData = (article: Article): ArticleFormData => ({
  title: article.title,
  slug: article.slug,
  category: article.category || '',
  extra_categories: article.extra_categories || [],
  excerpt: article.excerpt || '',
  seo_title: article.seo_title || '',
  seo_metadesc: article.seo_metadesc || '',
  author: article.author || DEFAULT_AUTHOR,
  published: article.published,
  published_at: article.published_at || null,
  content: article.content || '',
})

// ISO timestamp -> YYYY-MM-DD in local time, for <input type="date">
const toDateInput = (iso: string | null): string => {
  if (!iso) return ''
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

// Translate the most common Postgres errors to Danish
const saveErrorMessage = (err: any): string => {
  if (err?.code === '23505') return 'Slug er allerede i brug af en anden artikel'
  if (err?.code === '23514') return 'Ugyldig slug. Brug kun små bogstaver (a-z), tal og bindestreg'
  if (err?.code === '23503') return 'Den valgte kategori findes ikke'
  return err?.message || 'Ukendt fejl'
}

// Character counter helper text, same style as the SEO fields in LocationForm
const counterText = (value: string, max: number, hint?: string) => {
  const length = value.length
  return `${length}/${max} tegn${length > max ? '. For lang, Google afkorter' : ''}${hint ? `. ${hint}` : ''}`
}

export default function ArticleForm() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { isAdmin } = useAuth()
  const isEdit = Boolean(id)

  const [formData, setFormData] = useState<ArticleFormData>(EMPTY_FORM)
  // The slug follows the title until the user edits it (always locked for existing articles)
  const [slugTouched, setSlugTouched] = useState(false)
  // Slug and published state as saved in the database
  const [saved, setSaved] = useState<{ slug: string; published: boolean } | null>(null)
  const [snackbar, setSnackbar] = useState<{ open: boolean; severity: 'success' | 'error'; message: string }>({
    open: false,
    severity: 'success',
    message: '',
  })
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  // Which article the form was filled from, so a refetch never overwrites unsaved edits
  const loadedId = useRef<string | null>(null)

  const articleQuery = useQuery({
    queryKey: ['article', id],
    enabled: isAdmin && isEdit,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('articles')
        .select('*')
        .eq('id', id)
        .single()

      if (error) throw error
      return data as Article
    },
  })
  const categoriesQuery = useArticleCategories(isAdmin)
  const categories = categoriesQuery.data || []

  useEffect(() => {
    const article = articleQuery.data
    if (!article || loadedId.current === article.id) return
    loadedId.current = article.id
    setFormData(toFormData(article))
    setSaved({ slug: article.slug, published: article.published })
    setSlugTouched(true)
  }, [articleQuery.data])

  const showSnackbar = (severity: 'success' | 'error', message: string) =>
    setSnackbar({ open: true, severity, message })

  const saveMutation = useMutation({
    mutationFn: async (payload: Omit<Article, 'id' | 'created_at' | 'updated_at'>) => {
      if (isEdit) {
        const { data, error } = await supabase
          .from('articles')
          .update(payload)
          .eq('id', id)
          .select()
          .single()

        if (error) throw error
        return data as Article
      }

      const { data, error } = await supabase
        .from('articles')
        .insert([payload])
        .select()
        .single()

      if (error) throw error
      return data as Article
    },
    onSuccess: (article) => {
      loadedId.current = article.id
      queryClient.setQueryData(['article', article.id], article)
      queryClient.invalidateQueries({ queryKey: ['articles'] })
      setSaved({ slug: article.slug, published: article.published })
      setSlugTouched(true)
      // published_at may have been set to today on save
      setFormData((prev) => ({ ...prev, published_at: article.published_at || null }))
      showSnackbar('success', 'Artikel gemt!')
      if (!isEdit) {
        navigate(`/articles/edit/${article.id}`, { replace: true })
      }
    },
    onError: (err: any) => {
      showSnackbar('error', `Kunne ikke gemme: ${saveErrorMessage(err)}`)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase
        .from('articles')
        .delete()
        .eq('id', id)
        .select('id')

      if (error) throw error
      if (!data || data.length === 0) throw new Error('Artiklen blev ikke slettet (mangler rettigheder?)')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['articles'] })
      navigate('/articles')
    },
    onError: (err: any) => {
      setDeleteDialogOpen(false)
      showSnackbar('error', `Kunne ikke slette: ${err.message}`)
    },
  })

  const handleChange = (field: keyof ArticleFormData) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = e.target.value
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const title = e.target.value
    setFormData((prev) => ({ ...prev, title, slug: slugTouched ? prev.slug : slugify(title) }))
  }

  const handleSlugChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setSlugTouched(true)
    setFormData((prev) => ({ ...prev, slug: e.target.value }))
  }

  const handleCategoryChange = (category: string) => {
    // A category can't be both main and extra
    setFormData((prev) => ({
      ...prev,
      category,
      extra_categories: prev.extra_categories.filter((c) => c !== category),
    }))
  }

  const handlePublishedChange = (published: boolean) => {
    setFormData((prev) => ({
      ...prev,
      published,
      // Default to today the first time the article is published
      published_at: published && !prev.published_at ? new Date().toISOString() : prev.published_at,
    }))
  }

  const handlePublishedAtChange = (value: string) => {
    setFormData((prev) => ({
      ...prev,
      // Noon local time, so the date is the same in every time zone around Denmark
      published_at: value ? new Date(`${value}T12:00:00`).toISOString() : null,
    }))
  }

  const slug = formData.slug.trim()
  const slugValid = SLUG_PATTERN.test(slug)
  const slugChangedOnPublished = Boolean(saved?.published && slug !== saved.slug)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.title.trim()) {
      showSnackbar('error', 'Titel skal udfyldes')
      return
    }
    if (!slugValid) {
      showSnackbar('error', 'Ugyldig slug. Brug kun små bogstaver (a-z), tal og bindestreg')
      return
    }

    saveMutation.mutate({
      title: formData.title.trim(),
      slug,
      category: formData.category || null,
      extra_categories: formData.extra_categories.filter((c) => c !== formData.category),
      // Empty = fallback on the website (title / no description)
      excerpt: formData.excerpt.trim() || null,
      seo_title: formData.seo_title.trim() || null,
      seo_metadesc: formData.seo_metadesc.trim() || null,
      author: formData.author.trim() || DEFAULT_AUTHOR,
      published: formData.published,
      published_at: formData.published && !formData.published_at ? new Date().toISOString() : formData.published_at,
      content: formData.content,
    })
  }

  if (!isAdmin) return null

  if (isEdit && articleQuery.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (isEdit && articleQuery.error) {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/articles')} sx={{ mb: 2 }}>
          Tilbage
        </Button>
        <Alert severity="error">Kunne ikke hente artiklen: {(articleQuery.error as Error).message}</Alert>
      </Box>
    )
  }

  const saving = saveMutation.isPending
  const categoryName = (slugValue: string) => categories.find((c) => c.slug === slugValue)?.name || slugValue

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/articles')}
          sx={{ minWidth: 'auto' }}
        >
          Tilbage
        </Button>
        <Typography variant="h4" sx={{ fontWeight: 700, flexGrow: 1 }}>
          {isEdit ? 'Rediger artikel' : 'Opret ny artikel'}
        </Typography>
        {saved?.published && (
          <Button
            startIcon={<OpenInNewIcon />}
            href={articleUrl(saved.slug)}
            target="_blank"
            rel="noopener noreferrer"
          >
            Se på siden
          </Button>
        )}
      </Box>

      {categoriesQuery.error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Kunne ikke hente kategorier: {(categoriesQuery.error as Error).message}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 2fr) minmax(320px, 1fr)' },
            gap: 3,
            alignItems: 'start',
          }}
        >
          {/* Main column: title, slug, excerpt, content */}
          <Card>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ mb: 3 }}>
                <TextField
                  required
                  fullWidth
                  label="Titel"
                  value={formData.title}
                  onChange={handleTitleChange}
                  placeholder="F.eks. Muslimsk begravelse i Danmark"
                  helperText="H1 på siden"
                />
              </Box>

              <Box sx={{ mb: 3 }}>
                <TextField
                  required
                  fullWidth
                  label="Slug (URL-venlig)"
                  value={formData.slug}
                  onChange={handleSlugChange}
                  error={Boolean(slug) && !slugValid}
                  helperText={
                    slug && !slugValid
                      ? 'Kun små bogstaver (a-z), tal og bindestreg. Ingen æ, ø, å eller mellemrum.'
                      : `${articleUrl(slug || '...')}${slugTouched ? '' : ' (autogenereret fra titel, kan tilpasses)'}`
                  }
                />
                {slugChangedOnPublished && saved && (
                  <Alert severity="warning" sx={{ mt: 1.5 }}>
                    Artiklen er udgivet. Ændrer du slug, flytter den fra /{saved.slug}/ til /{slug}/, og links
                    til den gamle adresse virker ikke længere, medmindre der oprettes en redirect.
                  </Alert>
                )}
              </Box>

              <Box sx={{ mb: 3 }}>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  label="Uddrag"
                  value={formData.excerpt}
                  onChange={handleChange('excerpt')}
                  error={formData.excerpt.length > 160}
                  helperText={counterText(formData.excerpt, 160, 'Kort intro til oversigter og artikelkort')}
                />
              </Box>

              <Box>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                  Indhold
                </Typography>
                <Typography variant="caption" color="text.secondary" gutterBottom sx={{ display: 'block', mb: 1 }}>
                  Brug Overskrift 2 til afsnit. Interne links skrives som /sti/. Klik på en bedemandsliste for at ændre den.
                </Typography>
                <RichTextEditor
                  variant="article"
                  value={formData.content}
                  onChange={(html) => setFormData((prev) => ({ ...prev, content: html }))}
                  placeholder="Skriv artiklen her..."
                  minHeight={400}
                />
              </Box>
            </CardContent>
          </Card>

          {/* Side column: publishing, categories, SEO */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Card>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                  Udgivelse
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      color="success"
                      checked={formData.published}
                      onChange={(e) => handlePublishedChange(e.target.checked)}
                    />
                  }
                  label={formData.published ? 'Udgivet' : 'Kladde (ikke synlig på siden)'}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  type="date"
                  label="Udgivelsesdato"
                  value={toDateInput(formData.published_at)}
                  onChange={(e) => handlePublishedAtChange(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  helperText="Sættes til i dag, første gang artiklen udgives"
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Forfatter"
                  value={formData.author}
                  onChange={handleChange('author')}
                />
              </CardContent>
            </Card>

            <Card>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                  Kategorier
                </Typography>
                <FormControl fullWidth sx={{ mb: 2, mt: 1 }}>
                  <InputLabel>Kategori</InputLabel>
                  <Select
                    value={formData.category}
                    label="Kategori"
                    onChange={(e) => handleCategoryChange(e.target.value)}
                  >
                    <MenuItem value="">
                      <em>Ingen kategori</em>
                    </MenuItem>
                    {categories.map((c) => (
                      <MenuItem key={c.slug} value={c.slug}>
                        {c.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel>Ekstra kategorier</InputLabel>
                  <Select
                    multiple
                    value={formData.extra_categories}
                    label="Ekstra kategorier"
                    onChange={(e) => {
                      const value = e.target.value
                      setFormData((prev) => ({
                        ...prev,
                        extra_categories: typeof value === 'string' ? value.split(',') : value,
                      }))
                    }}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip key={value} label={categoryName(value)} size="small" />
                        ))}
                      </Box>
                    )}
                  >
                    {categories
                      .filter((c) => c.slug !== formData.category)
                      .map((c) => (
                        <MenuItem key={c.slug} value={c.slug}>
                          {c.name}
                        </MenuItem>
                      ))}
                  </Select>
                  <FormHelperText>Artiklen vises også i disse kategorier</FormHelperText>
                </FormControl>
              </CardContent>
            </Card>

            <Card>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                  SEO
                </Typography>
                <TextField
                  fullWidth
                  label="SEO titel"
                  value={formData.seo_title}
                  onChange={handleChange('seo_title')}
                  error={formData.seo_title.length > 60}
                  helperText={counterText(formData.seo_title, 60, 'Tom = artiklens titel')}
                  sx={{ mb: 2, mt: 1 }}
                />
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label="Meta-beskrivelse"
                  value={formData.seo_metadesc}
                  onChange={handleChange('seo_metadesc')}
                  error={formData.seo_metadesc.length > 155}
                  helperText={counterText(formData.seo_metadesc, 155)}
                />
              </CardContent>
            </Card>
          </Box>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ mt: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
          {isEdit && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setDeleteDialogOpen(true)}
              disabled={saving}
            >
              Slet artikel
            </Button>
          )}
          <Box sx={{ flexGrow: 1 }} />
          <Button
            variant="outlined"
            onClick={() => navigate('/articles')}
            disabled={saving}
          >
            Annuller
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="success"
            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
            disabled={saving}
          >
            {saving ? 'Gemmer...' : 'Gem artikel'}
          </Button>
        </Box>
      </form>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deleteMutation.isPending && setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Slet artikel?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Er du sikker på at du vil slette <strong>{formData.title || 'artiklen'}</strong>?
            <br /><br />
            Dette kan ikke fortrydes.
            {saved?.published && ` Artiklen er udgivet, så /${saved.slug}/ forsvinder fra find-bedemand.dk.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleteMutation.isPending}>
            Annuller
          </Button>
          <Button
            onClick={() => deleteMutation.mutate()}
            variant="contained"
            color="error"
            disabled={deleteMutation.isPending}
            startIcon={deleteMutation.isPending ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {deleteMutation.isPending ? 'Sletter...' : 'Slet permanent'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.severity === 'success' ? 3000 : 8000}
        onClose={(_, reason) => {
          if (reason !== 'clickaway') setSnackbar((prev) => ({ ...prev, open: false }))
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}
